<template>
  <div class="cd-summary">
    <b-row class="mb-5">
      <b-col cols="2">
        <b-input
          v-model="cycle"
          size="sm"
          placeholder="Kỳ (YYYY-MM)"
          v-on:keyup.enter="onSearch"
          autocomplete="off"
        />
      </b-col>
      <b-col cols="4">
        <b-input
          v-model="employeeName"
          size="sm"
          placeholder="Tìm theo mã NV hoặc tên NV"
          v-on:keyup.enter="onSearch"
          autocomplete="off"
        />
      </b-col>
      <b-col cols="1">
        <b-button
          size="sm"
          variant="primary"
          style="font-weight: 600; width: 80px"
          @click="onSearch"
        >
          Lọc
        </b-button>
      </b-col>
      <b-col cols="5" class="text-right">
        <b-button
          size="sm"
          variant="outline-success"
          class="mr-2"
          :disabled="exporting"
          @click="onExport"
        >
          <b-spinner v-if="exporting" small class="mr-1" />
          <i v-else class="far fa-file-excel cd-btn-icon"></i>
          Xuất Excel
        </b-button>
        <b-button size="sm" variant="outline-primary" @click="openImportModal">
          <i class="fas fa-file-import cd-btn-icon"></i>
          Nhập Excel
        </b-button>
        <input
          ref="importFileInput"
          type="file"
          accept=".xlsx,.xls"
          class="d-none"
          @change="onFileSelected"
        />
      </b-col>
    </b-row>

    <b-table
      :items="items"
      :fields="fields"
      :small="true"
      :busy="loading"
      responsive
      bordered
      hover
      show-empty
      empty-text="Không có dữ liệu"
    >
      <template #table-busy>
        <div class="text-center text-primary my-3">
          <b-spinner small class="align-middle mr-2"></b-spinner>
          <strong>Đang tải...</strong>
        </div>
      </template>

      <template #cell(employeeName)="data">
        <span>{{ data.item.employeeName }}</span>
        <i
          v-if="data.item.employeeStatus === 'INACTIVE'"
          v-b-tooltip.hover
          title="NV đã nghỉ việc — cân nhắc miễn nợ"
          class="fas fa-exclamation-triangle cd-warn-icon"
        ></i>
      </template>

      <template #cell(pendingAmount)="data">
        <span class="cd-amount-pending">{{
          format(data.item.pendingAmount)
        }}</span>
      </template>

      <template #cell(settledAmount)="data">
        {{ format(data.item.settledAmount) }}
      </template>

      <template #cell(waivedAmount)="data">
        {{ format(data.item.waivedAmount) }}
      </template>

      <template #cell(actions)="data">
        <div class="cd-action-btns">
          <b-button
            size="sm"
            variant="outline-primary"
            @click="emitDrilldown(data.item)"
          >
            <i class="fas fa-search cd-btn-icon"></i> Chi tiết
          </b-button>
          <b-button
            v-if="data.item.pendingAmount > 0"
            size="sm"
            variant="success"
            @click="openSettle(data.item)"
          >
            <i class="fas fa-money-check-alt cd-btn-icon"></i> Lập phiếu thu
          </b-button>
        </div>
      </template>
    </b-table>

    <b-row>
      <b-col>
        <p class="mt-3 text-dark" style="font-weight: 500">
          Tổng số: {{ total }}
        </p>
      </b-col>
      <b-col>
        <b-pagination-nav
          v-if="numberOfPage >= 2"
          class="custom-pagination"
          :link-gen="linkGen"
          :number-of-pages="numberOfPage"
          v-model="page"
          @change="onPageChange"
          align="right"
          first-class="page-item-first btn btn-icon mr-1 btn-sm my-1"
          prev-class="page-item-prev btn btn-icon mr-1 btn-sm my-1"
          next-class="page-item-next btn btn-icon btn-sm mr-1 my-1"
          last-class="page-item-last btn btn-icon btn-sm my-1"
          page-class="btn btn-icon btn-sm border-0 mr-1 my-1"
        >
          <template v-slot:first-text>
            <i class="ki ki-double-arrow-back icon-xs"></i>
          </template>
          <template v-slot:prev-text>
            <i class="ki ki-arrow-back icon-xs"></i>
          </template>
          <template v-slot:next-text>
            <i class="ki ki-arrow-next icon-xs"></i>
          </template>
          <template v-slot:last-text>
            <i class="ki ki-double-arrow-next icon-xs"></i>
          </template>
        </b-pagination-nav>
      </b-col>
    </b-row>

    <SettleDebtDialog
      v-model="settleDialog"
      :employee="selectedEmployee"
      :cycle="cycle"
      @settled="onSettled"
    />

    <b-modal
      v-model="importResultModal"
      title="Kết quả nhập Excel"
      size="md"
      centered
      ok-only
      ok-title="Đóng"
    >
      <div v-if="importing" class="text-center py-3">
        <b-spinner class="mb-2"></b-spinner>
        <p>Đang xử lý file...</p>
      </div>
      <div v-else-if="importResult">
        <p>
          Tổng số dòng: <strong>{{ importResult.totalRows }}</strong>
        </p>
        <p class="text-success">
          Đã thu thành công: <strong>{{ importResult.success }}</strong>
        </p>
        <p class="text-warning">
          Bỏ qua (đã SETTLED/WAIVED hoặc Action không Y):
          <strong>{{ importResult.skipped }}</strong>
        </p>
        <p class="text-danger">
          Lỗi: <strong>{{ importResult.failed }}</strong>
        </p>
        <b-table
          v-if="importResult.errors && importResult.errors.length"
          :items="importResult.errors"
          :fields="errorFields"
          small
          bordered
          class="mt-3"
        ></b-table>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { CommissionDebtService } from '@/core/services/commission-debt.service';
import SettleDebtDialog from './SettleDebtDialog.vue';

export default {
  name: 'SummaryTab',
  components: { SettleDebtDialog },
  data() {
    return {
      cycle: new Date().toISOString().slice(0, 7),
      employeeName: '',
      items: [],
      total: 0,
      page: 1,
      limit: 20,
      loading: false,
      settleDialog: false,
      selectedEmployee: null,
      exporting: false,
      importing: false,
      importResultModal: false,
      importResult: null,
      errorFields: [
        {
          key: 'row',
          label: 'Dòng',
          tdClass: 'text-center',
          thStyle: { width: '20%' },
        },
        { key: 'reason', label: 'Lý do' },
      ],
      linkGen: (pageNum) => (pageNum === 1 ? '?' : `?page=${pageNum}`),
      fields: [
        {
          key: 'employeeCode',
          label: 'Mã NV',
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { width: '8%' },
        },
        {
          key: 'employeeName',
          label: 'Tên nhân viên',
          thClass: 'text-center',
        },
        {
          key: 'contractCount',
          label: 'Số HĐ',
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { width: '7%' },
        },
        {
          key: 'voucherCount',
          label: 'Số voucher đã áp',
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { width: '10%' },
        },
        {
          key: 'pendingAmount',
          label: 'Chờ thu',
          thClass: 'text-center',
          tdClass: 'text-right',
          thStyle: { width: '12%' },
        },
        {
          key: 'settledAmount',
          label: 'Đã thu',
          thClass: 'text-center',
          tdClass: 'text-right',
          thStyle: { width: '12%' },
        },
        {
          key: 'waivedAmount',
          label: 'Đã miễn',
          thClass: 'text-center',
          tdClass: 'text-right',
          thStyle: { width: '12%' },
        },
        {
          key: 'actions',
          label: '',
          thClass: 'text-center',
          tdClass: 'cd-actions-cell',
          thStyle: { width: '17%' },
        },
      ],
    };
  },
  computed: {
    numberOfPage() {
      return Math.ceil(this.total / this.limit) || 1;
    },
  },
  mounted() {
    this.reload();
  },
  methods: {
    async reload() {
      if (!/^\d{4}-\d{2}$/.test(this.cycle)) {
        this.$bvToast?.toast?.('Kỳ phải có dạng YYYY-MM', {
          variant: 'danger',
          title: 'Lỗi',
        });
        return;
      }
      this.loading = true;
      try {
        const res = await CommissionDebtService.summary({
          cycle: this.cycle,
          employeeName: this.employeeName || undefined,
          page: this.page,
          limit: this.limit,
        });
        // BE wrap qua ResponseInterceptor → res.data.data.data
        const payload = res?.data?.data?.data || {};
        this.items = payload.items || [];
        this.total = payload.total || 0;
      } catch (e) {
        this.items = [];
        this.total = 0;
        const status = e?.response?.status;
        const msg =
          e?.response?.data?.message || e?.message || 'Lỗi không xác định';
        // eslint-disable-next-line no-console
        console.error('[SummaryTab] reload error:', status, msg, e);
        this.$bvToast?.toast?.(`API lỗi (${status || '?'}): ${msg}`, {
          variant: 'danger',
          title: 'Không tải được dữ liệu',
          autoHideDelay: 8000,
        });
      } finally {
        this.loading = false;
      }
    },
    onSearch() {
      this.page = 1;
      this.reload();
    },
    onPageChange(p) {
      this.page = p;
      this.reload();
    },
    format(n) {
      return Number(n || 0).toLocaleString('vi-VN');
    },
    emitDrilldown(item) {
      this.$emit('drilldown', {
        employeeId: item.employeeId,
        cycle: this.cycle,
      });
    },
    openSettle(item) {
      this.selectedEmployee = item;
      this.settleDialog = true;
    },
    onSettled() {
      this.settleDialog = false;
      this.reload();
    },
    async onExport() {
      if (!/^\d{4}-\d{2}$/.test(this.cycle)) {
        this.$bvToast?.toast?.('Kỳ phải có dạng YYYY-MM', {
          variant: 'danger',
          title: 'Lỗi',
        });
        return;
      }
      this.exporting = true;
      try {
        const res = await CommissionDebtService.exportExcel({
          cycle: this.cycle,
          employeeName: this.employeeName || undefined,
        });
        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `commission-debt-${this.cycle}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.$bvToast?.toast?.('Đã tải file Excel', {
          variant: 'success',
          title: 'Thành công',
          autoHideDelay: 3000,
        });
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || 'Lỗi';
        this.$bvToast?.toast?.(`Xuất Excel lỗi: ${msg}`, {
          variant: 'danger',
          title: 'Lỗi',
          autoHideDelay: 6000,
        });
      } finally {
        this.exporting = false;
      }
    },
    openImportModal() {
      this.$refs.importFileInput.click();
    },
    async onFileSelected(event) {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;

      this.importing = true;
      this.importResult = null;
      this.importResultModal = true;
      try {
        const res = await CommissionDebtService.importSettle(file);
        this.importResult = res?.data?.data?.data ||
          res?.data?.data || {
            totalRows: 0,
            success: 0,
            skipped: 0,
            failed: 0,
            errors: [],
          };
        if (this.importResult.success > 0) {
          this.reload();
        }
      } catch (e) {
        const msg =
          e?.response?.data?.message || e?.message || 'Lỗi không xác định';
        this.importResult = {
          totalRows: 0,
          success: 0,
          skipped: 0,
          failed: 1,
          errors: [{ row: '-', reason: msg }],
        };
      } finally {
        this.importing = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.cd-summary {
  .cd-warn-icon {
    color: #f59e0b !important;
    margin-left: 6px;
    font-size: 13px;
    cursor: help;
  }
  .cd-amount-pending {
    color: #f59e0b;
    font-weight: 600;
  }
  .cd-btn-icon {
    color: inherit !important;
    font-size: 11px;
    margin-right: 3px;
  }
  .cd-action-btns {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    justify-content: center;
  }
}
</style>
